import { useNavigate } from 'react-router-dom';
import { CourseInstructorDetail } from '../../../mocks/courses';
import { sanitizeRichHtml } from '@/lib/richText';

interface Props {
  instructor: CourseInstructorDetail;
}

const CourseInstructor = ({ instructor }: Props) => {
  const navigate = useNavigate();
  const richBio = sanitizeRichHtml(instructor.bio);

  return (
    <div>
      <h2 className="text-xl font-bold text-[#0A1628] mb-5">講師介紹</h2>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        {/* Top: avatar + name + title */}
        <div className="flex items-start gap-5 mb-6">
          <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 bg-slate-100">
            <img
              src={instructor.avatar}
              alt={instructor.name}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-[#0A1628] mb-1">{instructor.name}</h3>
            <p className="text-slate-500 text-sm mb-2">{instructor.title}</p>
            <div className="flex items-center gap-2">
              {instructor.companyLogo ? (
                <div className="w-5 h-5 rounded overflow-hidden flex-shrink-0 bg-slate-100">
                  <img
                    src={instructor.companyLogo}
                    alt={instructor.company}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-5 h-5 rounded overflow-hidden flex-shrink-0 bg-violet-50 text-violet-600 flex items-center justify-center">
                  <i className={`${instructor.companyIcon || 'ri-apps-line'} text-xs`} />
                </div>
              )}
              <span className="text-sm font-semibold text-violet-600">{instructor.company}</span>
              <span className="text-slate-300">·</span>
              <span className="text-xs text-slate-400">{instructor.experience}</span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {instructor.stats.map((stat) => (
            <div key={stat.label} className="text-center bg-slate-50 rounded-xl py-3 px-2">
              <div className="text-base font-bold text-[#0A1628] mb-0.5">{stat.value}</div>
              <div className="text-xs text-slate-400 leading-tight">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Bio */}
        <div className="mb-5">
          <h4 className="text-sm font-semibold text-[#0A1628] mb-2">講師背景</h4>
          <div
            className="mentor-rich-content text-slate-500 text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: richBio }}
          />
        </div>

        {/* Expertise tags */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-[#0A1628] mb-3">專長領域</h4>
          <div className="flex flex-wrap gap-2">
            {instructor.expertise.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 bg-violet-50 text-violet-600 text-xs font-semibold rounded-lg border border-violet-100"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Mentor booking CTA */}
        {instructor.mentorId && (
          <div className="border-t border-slate-100 pt-5">
            <div className="flex items-center justify-between bg-[#F7F8FB] rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white">
                  <img
                    src={instructor.avatar}
                    alt={instructor.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#0A1628]">
                    想直接和 {instructor.name} 深聊？
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    預約 1v1 導師諮詢，量身定制你的職涯路徑
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/mentor/${instructor.mentorId}`)}
                className="flex-shrink-0 ml-4 px-4 py-2.5 bg-[#0A1628] hover:bg-[#0d1f3c] text-white text-xs font-bold rounded-xl cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-calendar-check-line" />
                預約諮詢
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseInstructor;
